import { LegacyPortalAccountService } from "@spwn-portal/grpc";
/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
/* eslint-enable no-restricted-imports */
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useConnectClient } from "utility/connectWeb";

/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import "firebase/compat/auth";
/* eslint-enable no-restricted-imports */

import appConfig from "../../constants/appConfig";
import { useQuery } from "../../utility/useQuery";
import { encodeReturnUrl } from "./AccountsAuthenticator";
import {
  forgetState,
  keepState,
  STATE_PATTERN,
  toBridgeUrl,
  toInteractionUrl,
} from "./oidcBridgeState";

type OidcBridgeStatus =
  | { type: "loading" }
  | { type: "submitting"; action: string; code: string }
  | { type: "error" };

/**
 * 同じ参照を使い回す
 *
 * 毎回オブジェクトを作ると、`onAuthStateChanged` の発火で effect が再実行された
 * ときに status の識別子が変わり、購読側の effect（alert）が二度走る
 */
const ERROR: OidcBridgeStatus = { type: "error" };

/** onAuthStateChanged の初回通知は非同期に来る。それまでを signedOut と区別する */
type AuthState =
  | { type: "unknown" }
  | { type: "signedOut" }
  | { type: "signedIn"; user: firebase.User };

const UNKNOWN: AuthState = { type: "unknown" };

/**
 * auth-op へ本人確認を受け渡す
 *
 * auth-op から `?state=<認可リクエストの識別子>` で来る。ログイン状態を確かめ、
 * backend から一回限りのコードを得て、auth-op へ form POST で返す。
 * ユーザーの操作は挟まない。
 */
export const useOidcBridge = (): OidcBridgeStatus => {
  const history = useHistory();
  const query = useQuery();
  const client = useConnectClient(LegacyPortalAccountService);

  const [status, setStatus] = useState<OidcBridgeStatus>({ type: "loading" });
  const [auth, setAuth] = useState<AuthState>(UNKNOWN);

  /** 発行中に effect が再実行されてもコードを二重に取りに行かない */
  const issuing = useRef(false);

  const state = query.get("state") ?? undefined;

  /** ログイン状態は Firebase から直接見る */
  useEffect(
    () =>
      firebase
        .auth()
        .onAuthStateChanged((user) =>
          setAuth(
            user === null ? { type: "signedOut" } : { type: "signedIn", user }
          )
        ),
    []
  );

  useEffect(() => {
    if (issuing.current) return;

    if (state === undefined || !STATE_PATTERN.test(state)) {
      setStatus(ERROR);
      return;
    }

    const action = toInteractionUrl(appConfig.accountsApp.authOpOrigin, state);
    if (action === undefined) {
      // env の設定漏れでしか起きない。本番の console は潰されるが dev では見える
      console.error("authOpOrigin is not usable");
      setStatus(ERROR);
      return;
    }

    if (auth.type === "unknown") return;

    const toLogin = () => {
      keepState(state);
      const returnUrl = toBridgeUrl(state);
      history.push(`/login?return_url=${encodeReturnUrl({ returnUrl })}`);
    };

    if (auth.type === "signedOut") {
      toLogin();
      return;
    }

    /**
     * Shopify は id_token の email で顧客の連絡先を作る。backend の
     * AuthNInterceptor も未検証を弾くため、先に済ませてもらう
     */
    if (!auth.user.emailVerified) {
      keepState(state);
      history.push("/signup/authentication");
      return;
    }

    issuing.current = true;
    void (async () => {
      /*
       * 失効していないかを Firebase に確かめてから発行してもらう。
       *
       * キャッシュ済みの ID トークンは最長1時間生き残り、backend も
       * `checkRevoked` を渡さないため、他のサービスでログアウトした直後でも
       * 素通りしてしまう。auth-op の引き換えがそれを弾くので、確かめずに進むと
       * 利用者にはログイン画面ではなく RP のエラーだけが見える
       */
      try {
        await auth.user.getIdToken(true);
      } catch (error) {
        /*
         * 失効なら Firebase がサインアウトするのでログイン画面が出る。通信断や
         * クォータ超過もここへ来るが、その場合もログインからやり直せる。
         *
         * `issuing` は戻さない。サインアウトで effect が再実行され、未ログインの
         * 分岐がもう一度 /login を積んでしまう
         */
        console.error(error);
        toLogin();
        return;
      }

      try {
        const res = await client.issueOidcLoginCode({
          oidcTransactionId: state,
        });

        // 中断した往復の残りが後日のログインで引き戻さないようにする
        forgetState();
        setStatus({ type: "submitting", action, code: res.code });
      } catch (error) {
        setStatus(ERROR);
        console.error(error);
      }
    })();
  }, [auth, client, history, state]);

  return status;
};
